import { useEffect, useState } from "react";
import { Globe, Plus, Trash2, X } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api } from "@/lib/api";
import { LANGS } from "@/lib/i18n";
import { toast } from "sonner";

export default function StaffPoliciesPage() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [doc, setDoc] = useState(null);
  const [lang, setLang] = useState("en");
  const [showAdd, setShowAdd] = useState(false);

  function load() {
    api.get("/policies").then((r) => {
      setList(r.data);
      if (r.data.length) {
        const found = r.data.find((p) => p.slug === active) || r.data[0];
        setActive(found.slug);
        setDoc({ ...found });
      } else {
        setActive(null); setDoc(null);
      }
    });
  }
  useEffect(load, []);

  function pick(slug) {
    const p = list.find((x) => x.slug === slug);
    setActive(slug); setDoc({ ...p }); setLang("en");
  }

  function setLocalized(field, value) {
    if (lang === "en") setDoc({ ...doc, [field]: value });
    else {
      const i18n = { ...(doc.i18n || {}) };
      i18n[lang] = { ...(i18n[lang] || {}), [field]: value };
      setDoc({ ...doc, i18n });
    }
  }
  function getLocalized(field) {
    if (lang === "en") return doc?.[field] ?? "";
    return doc?.i18n?.[lang]?.[field] ?? "";
  }

  async function save() {
    await api.put(`/policies/${doc.slug}`, { slug: doc.slug, title: doc.title, content: doc.content, i18n: doc.i18n || {} });
    toast.success("Policy saved");
    load();
  }

  async function del(slug) {
    if (!confirm(`Delete policy "${slug}"?`)) return;
    await api.delete(`/policies/${slug}`);
    toast.success("Deleted");
    setActive(null); setDoc(null);
    load();
  }

  return (
    <StaffLayout>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-display text-4xl">Policies</h1>
          <p className="text-sm text-[#71717A] mt-1">Edit, add or remove policies. Each supports 5 languages.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="policy-add" className="bg-brand-primary text-white px-5 py-2.5 rounded-sm text-sm flex items-center gap-2">
          <Plus size={14} /> New policy
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="bg-white border border-[#E4E4E7] rounded-md p-3">
          {list.map((p) => (
            <div key={p.slug} className="flex items-center gap-1">
              <button
                onClick={() => pick(p.slug)}
                data-testid={`staff-policy-${p.slug}`}
                className={`flex-1 text-left px-3 py-2 rounded-sm text-sm truncate ${active === p.slug ? "bg-brand-secondary text-brand-primary" : "hover:bg-[#FAFAFA]"}`}
              >{p.title}</button>
              <button data-testid={`policy-del-${p.slug}`} onClick={() => del(p.slug)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm"><Trash2 size={13} /></button>
            </div>
          ))}
          {list.length === 0 && <div className="text-sm text-[#71717A] p-3">No policies. Add one →</div>}
        </aside>

        <div className="lg:col-span-3 bg-white border border-[#E4E4E7] rounded-md">
          {doc ? (
            <>
              <div className="flex items-center gap-2 border-b px-6 py-3">
                <Globe size={14} className="text-brand-muted" />
                {LANGS.map((l) => (
                  <button key={l.code} data-testid={`policy-lang-${l.code}`} onClick={() => setLang(l.code)}
                    className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm ${lang === l.code ? "bg-brand-primary text-white" : "text-[#71717A] hover:bg-[#FAFAFA]"}`}>{l.flag} {l.code}</button>
                ))}
                <span className="ml-auto text-xs text-[#71717A]">slug: {doc.slug}</span>
              </div>
              <div className="p-6 space-y-4">
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-[#71717A]">Title ({lang})</span>
                  <input data-testid="policy-title" value={getLocalized("title")} onChange={(e) => setLocalized("title", e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-wider text-[#71717A]">Content ({lang})</span>
                  <textarea data-testid="policy-content" rows={14} value={getLocalized("content")} onChange={(e) => setLocalized("content", e.target.value)} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm font-sans" />
                </label>
                <div className="flex justify-end">
                  <button data-testid="policy-save" onClick={save} className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Save policy</button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-12 text-center text-[#71717A]">Select or add a policy.</div>
          )}
        </div>
      </div>

      {showAdd && <AddPolicyModal onClose={() => setShowAdd(false)} onCreated={(slug) => { setShowAdd(false); load(); setTimeout(() => pick(slug), 200); }} />}
    </StaffLayout>
  );
}

function AddPolicyModal({ onClose, onCreated }) {
  const [f, setF] = useState({ slug: "", title: "", content: "" });
  async function submit(e) {
    e.preventDefault();
    const slug = f.slug.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-|-$/g, "");
    if (!slug) { toast.error("Invalid slug"); return; }
    try {
      await api.post("/policies", { slug, title: f.title, content: f.content });
      toast.success("Policy created");
      onCreated(slug);
    } catch (e2) { toast.error(e2.response?.data?.detail || "Failed"); }
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white w-full max-w-md rounded-md">
        <div className="p-5 border-b flex justify-between items-center">
          <h2 className="font-display text-2xl">New policy</h2>
          <button type="button" onClick={onClose} className="p-2"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Slug (URL part)</span>
            <input data-testid="new-policy-slug" required value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="e.g. pet-policy" className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Title (English)</span>
            <input data-testid="new-policy-title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Content (English)</span>
            <textarea data-testid="new-policy-content" required rows={6} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} className="mt-1 w-full border border-[#E4E4E7] rounded-sm px-3 py-2 text-sm" />
          </label>
        </div>
        <div className="p-5 border-t flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
          <button data-testid="new-policy-save" type="submit" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Create</button>
        </div>
      </form>
    </div>
  );
}
